import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { http } from './api'
import { errorMessage } from './errors'
import { beginPlanningEdit, PLANNING_REFRESH_MS, usePlanningEditing } from './planning-refresh'
import type {
  HistorySuggestion,
  HistorySuggestionRow,
  MonthKey,
  Opportunity,
  OpportunityInput,
  OpportunityStatus,
  PlanningOverview,
  PlanningStage,
} from './planning-types'

export const planningKeys = {
  overview: (from?: MonthKey, months?: number, stages?: PlanningStage[]) =>
    ['planning', 'overview', from ?? 'now', months ?? 0, stages?.join(',') ?? 'alles'] as const,
  history: (month: MonthKey) => ['planning', 'history', month] as const,
}

export const planningApi = {
  /**
   * De keuze gaat altijd expliciet mee zodra hij er is: de server geeft 422 op een
   * lege lijst, dus een keuze die onderweg leegloopt hoort hier al op te vallen en
   * niet stilletjes als "alles" aan te komen.
   */
  overview(from?: MonthKey, months?: number, stages?: PlanningStage[]) {
    return http.get<PlanningOverview>('/api/planning/overview', {
      from: from ?? undefined,
      months: months ?? undefined,
      stages: stages && stages.length > 0 ? stages.join(',') : undefined,
    })
  },
  createOpportunity(input: OpportunityInput) {
    return http.post<Opportunity>('/api/planning/opportunities', input)
  },
  updateOpportunity(id: string, input: OpportunityInput) {
    return http.patch<Opportunity>(`/api/planning/opportunities/${id}`, input)
  },
  setStatus(id: string, status: OpportunityStatus, clientId?: string | null, projectId?: string | null) {
    return http.post<Opportunity>(`/api/planning/opportunities/${id}/status`, {
      status,
      clientId: clientId ?? undefined,
      projectId: projectId ?? undefined,
    })
  },
  reorderOpportunities(ids: string[]) {
    return http.put<void>('/api/planning/opportunities/order', { ids })
  },
  deleteOpportunity(id: string) {
    return http.del<void>(`/api/planning/opportunities/${id}`)
  },
  setAllocation(id: string, personId: string | null, month: MonthKey, hours: string) {
    return http.put<Opportunity>(`/api/planning/opportunities/${id}/allocations`, { personId, month, hours })
  },
  setAllocations(id: string, rows: { personId: string | null; month: MonthKey; hours: string }[]) {
    return http.put<Opportunity>(`/api/planning/opportunities/${id}/allocations/bulk`, { allocations: rows })
  },
  /** Positief is later, negatief eerder; uren en bedragen schuiven samen mee. */
  shiftOpportunity(id: string, months: number) {
    return http.post<Opportunity>(`/api/planning/opportunities/${id}/shift`, { months })
  },
  setRevenue(id: string, month: MonthKey, amount: string) {
    return http.put<Opportunity>(`/api/planning/opportunities/${id}/revenues`, { month, amount })
  },
  setCapacity(personId: string, month: MonthKey, hours: string | null) {
    return http.put<{ ok: true }>('/api/planning/capacity', { personId, month, hours })
  },
  historySuggestion(month: MonthKey) {
    return http.get<HistorySuggestion>('/api/planning/history-suggestion', { month })
  },
  applyHistorySuggestion(rows: HistorySuggestionRow[]) {
    return http.post<{ applied: number; createdOpportunities: number }>('/api/planning/history-suggestion/apply', {
      rows: rows.map((row) => ({
        personId: row.personId,
        clientId: row.clientId,
        opportunityId: row.opportunityId,
        month: row.month,
        hours: row.hours,
      })),
    })
  },
}

/**
 * Het overzicht haalt zichzelf op de achtergrond op, zodat je ziet wat een
 * collega verzet zonder te verversen. Gericht op deze query en niet globaal: de
 * rest van de app heeft daar niet om gevraagd.
 *
 * `refetchIntervalInBackground` staat expliciet uit (ook de standaard), zodat een
 * tabblad dat achter een ander ligt niets ophaalt. Terugkeren in het tabblad
 * haalt wel meteen op, met `always` in plaats van `true`, want de globale
 * `staleTime` zou een verversing binnen een halve minuut anders overslaan.
 */
export function usePlanningOverview(from?: MonthKey, months?: number, stages?: PlanningStage[]) {
  const editing = usePlanningEditing()

  return useQuery({
    queryKey: planningKeys.overview(from, months, stages),
    queryFn: () => planningApi.overview(from, months, stages),
    // Een cel bijwerken vervangt het hele overzicht; zonder dit knippert het raster
    // bij elke aanslag terug naar een laadstaat.
    placeholderData: (previous) => previous,
    refetchInterval: editing ? false : PLANNING_REFRESH_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: editing ? false : 'always',
  })
}

/**
 * Het voorstel uit de historie ververst bewust niet mee: het is een momentopname
 * die je in het venster zit te wijzigen, en binnenkomende gegevens zetten die
 * regels terug naar het voorstel.
 */
export function useHistorySuggestion(month: MonthKey, enabled: boolean) {
  return useQuery({
    queryKey: planningKeys.history(month),
    queryFn: () => planningApi.historySuggestion(month),
    enabled,
  })
}

/**
 * Elke mutatie ververst het hele overzicht: uren in een cel veranderen de
 * capaciteitsbalk, de omzet en de signalen tegelijk, dus deelvernieuwing zou
 * altijd iets laten staan dat niet meer klopt.
 */
export function usePlanningMutation<TArgs extends unknown[], TResult>(
  fn: (...args: TArgs) => Promise<TResult>,
  successMessage?: (result: TResult) => string,
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (args: TArgs) => fn(...args),
    // Een schrijfactie die nog onderweg is telt als bewerken: een verversing die
    // net voor het antwoord binnenkomt laat de cel eerst terugspringen naar de
    // oude waarde en daarna weer vooruit.
    onMutate: () => ({ release: beginPlanningEdit() }),
    onSettled: (_result, _error, _args, context) => context?.release(),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['planning'] })
      const message = successMessage?.(result)
      if (message) toast.success(message)
    },
    onError: (error) => toast.error(errorMessage(error, 'Opslaan mislukt')),
  })
}
